"use client";

/**
 * Captures `$pageview` on App Router navigations. Next's client router does
 * SPA transitions, so PostHog's automatic pageview capture is disabled
 * (`capture_pageview: false`) and we fire it here on pathname change.
 *
 * Pathname only (not search params) on purpose:
 *   - marketing UTM params land on the first load's $current_url anyway, and
 *   - on /dashboard the scrubber strips the query string, so watching search
 *     params would just emit duplicate scrubbed pageviews.
 * Avoiding useSearchParams also keeps marketing pages statically rendered.
 *
 * /staff is skipped entirely (the before_send scrubber drops it too, as a
 * belt-and-suspenders).
 */

import * as React from "react";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";

// Session replay runs ONLY on these public marketing pages (with masked
// inputs). Everywhere else — auth, /dashboard, /staff — recording is stopped
// on entry, so no authenticated screen (BOM / PCN / MPN) is ever recorded (§3).
const PUBLIC_MARKETING_PATHS: ReadonlySet<string> = new Set([
  "/",
  "/enterprise",
  "/security",
  "/privacy",
  "/terms",
  "/contact",
]);

export function PostHogPageView() {
  const pathname = usePathname();
  const posthog = usePostHog();

  React.useEffect(() => {
    if (!posthog || !pathname) return;

    // Marketing-only session replay: explicitly start on public pages and stop
    // everywhere else (auth / dashboard / staff), so it can't leak a screen of
    // BOM data even if remote config tried to enable it.
    if (PUBLIC_MARKETING_PATHS.has(pathname)) {
      posthog.startSessionRecording();
    } else {
      posthog.stopSessionRecording();
    }

    if (pathname.startsWith("/staff")) return; // internal tooling: no analytics
    // posthog fills $current_url (incl. query) from window.location; the
    // before_send scrubber removes the query on /dashboard.
    posthog.capture("$pageview");
  }, [pathname, posthog]);

  return null;
}
